import React from 'react';

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, icon }) => {
  console.log('Rendering feature card with inline icon and title:', title);
  
  return (
    <div className="p-6 bg-white rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col items-start text-left h-full">
      <div className="flex items-center mb-3">
        <div className="inline-flex items-center justify-center rounded-lg bg-lynx-lightest text-lynx-primary p-2 mr-3 flex-shrink-0">
          <div className="w-5 h-5 relative overflow-hidden">
            {icon}
          </div>
        </div>
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed">{description}</p>
    </div>
  );
};

const Features: React.FC = () => {
  console.log('Rendering Features section with inline icons and updated titles');
  const features = [
    {
      title: "Trial Lesson Bundles",
      description: "Sample lessons with multiple teachers—find your fit before you commit.",
      icon: (
        <svg className="absolute inset-0 w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      )
    },
    {
      title: "Smart Scheduling",
      description: "Flexible booking tools that work with your calendar, not against it.",
      icon: (
        <svg className="absolute inset-0 w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "Progress Milestones",
      description: "Track achievements, unlock badges, and get real feedback from your teacher.",
      icon: (
        <svg className="absolute inset-0 w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: "Practice Toolkit",
      description: "Access sheet music, technique guides, and expert practice routines tailored to your goals.",
      icon: (
        <svg className="absolute inset-0 w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    },
    {
      title: "Virtual Studio",
      description: "Join video lessons, submit recordings, and review notes—all in one place.",
      icon: (
        <svg className="absolute inset-0 w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "Thriving Community",
      description: "Connect with fellow students, join challenges, and celebrate your progress.",
      icon: (
        <svg className="absolute inset-0 w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21v-3a3 3 0 00-3-3H9M3 21a3 3 0 01-3-3V6a3 3 0 013-3h12a3 3 0 013 3v12a3 3 0 01-3 3z" />
        </svg>
      )
    }
  ];

  return (
    <section className="py-20 bg-lynx-lightest">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Tools to Unlock Your Musical Potential
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            LessonLynx provides all the tools and features you need to excel in your musical learning adventure. Discover how we make finding teachers, booking lessons, and tracking your progress easier than ever.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features; 